import React, { ChangeEvent, useEffect } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { ICarrier, ICustomer } from '@/types';
import { TextField, Grid, Typography, Divider } from '@mui/material';
import CustomDatePicker from '@/components/common/CommonDatePicker';
import { NumericInput } from '@/components/ui';

interface InsuranceFormProps {
  open?: any;
  InsurerDocsUploadComponent: React.ComponentType<any>;
}

const InsuranceForm: React.FC<InsuranceFormProps> = ({ open, InsurerDocsUploadComponent }) => {
  const form = useFormContext<ICarrier | ICustomer>();

  const commercialIssueDate = form.watch('commercialGeneralLiability.issueDate');
  const commercialExpiryDate = form.watch('commercialGeneralLiability.expiryDate');

  useEffect(() => {
    const isEditMode = typeof open === 'object' && (open as any)?._id;

    if (!isEditMode) {
      const automobileLiability = form.watch('automobileLiability');
      const cargoLiability = form.watch('cargoLiability');

      if (!automobileLiability?.expiryDate) {
        form.setValue('automobileLiability.expiryDate', commercialExpiryDate);
      }
      if (!automobileLiability?.issueDate) {
        form.setValue('automobileLiability.issueDate', commercialIssueDate);
      }

      if (!cargoLiability?.expiryDate) {
        form.setValue('cargoLiability.expiryDate', commercialExpiryDate);
      }
      if (!cargoLiability?.issueDate) {
        form.setValue('cargoLiability.issueDate', commercialIssueDate);
      }
    }
  }, [commercialIssueDate, commercialExpiryDate, open]);

  const handleDateChange = (e: any) => {
    try {
      const value = e.target.value;
      form.setValue(e.target.name, value);
    } catch (error) {
      console.warn("error", error);
    }
  };

  return (
    <Grid container spacing={1.75}>
      <Grid item xs={12} md={4}>
        <Controller
          name="insurerCompany"
          control={form.control}
          render={({ field }) => (
            <TextField
              size='small'
              {...field}
              error={!!form.formState.errors.insurerCompany}
              helperText={form.formState.errors.insurerCompany?.message}
              fullWidth
              label="Insurer Company"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="agentName"
          control={form.control}
          render={({ field }) => (
            <TextField
              size='small'
              {...field}
              error={!!form.formState.errors.agentName}
              helperText={form.formState.errors.agentName?.message}
              fullWidth
              label="Agent Name"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="agentAddress"
          control={form.control}
          render={({ field }) => (
            <TextField
              size='small'
              {...field}
              error={!!form.formState.errors.agentAddress}
              helperText={form.formState.errors.agentAddress?.message}
              fullWidth
              label="Agent Address"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="agentEmail"
          control={form.control}
          render={({ field }) => (
            <TextField
              size='small'
              {...field}
              error={!!form.formState.errors.agentEmail}
              helperText={form.formState.errors.agentEmail?.message}
              fullWidth
              label="Agent Email"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="agentPhoneNumber"
          control={form.control}
          render={({ field }) => (
            <TextField
              size='small'
              {...field}
              onChange={(e) => {
                field.onChange(e);
              }}
              error={!!form.formState.errors.agentPhoneNumber}
              helperText={form.formState.errors.agentPhoneNumber?.message}
              fullWidth
              label="Agent Phone Number"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="agentExtentionNo"
          control={form.control}
          render={({ field }) => (
            <TextField
              size='small'
              {...field}
              onChange={(e) => {
                field.onChange(e);
              }}
              error={!!form.formState.errors.agentExtentionNo}
              helperText={form.formState.errors.agentExtentionNo?.message}
              fullWidth
              label="Agent Extention No"
            />
          )}
        />
      </Grid>

      {/* Commercial General Liability */}
      <Grid item xs={12}>
        <Typography fontSize={{xs:13, md:14}} sx={{ mt: 0, fontWeight: '600'}}>
          Commercial General Liability
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="commercialGeneralLiability.issueDate"
          control={form.control}
          render={({ field }) => (
            <CustomDatePicker
              size='small'
              fullWidth
              name="commercialGeneralLiability.issueDate"
              label="Issue Date"
              value={form.watch('commercialGeneralLiability.issueDate')}
              onChange={handleDateChange}
              error={!!form.formState.errors.commercialGeneralLiability?.issueDate}
              helperText={form.formState.errors.commercialGeneralLiability?.issueDate?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="commercialGeneralLiability.expiryDate"
          control={form.control}
          render={({ field }) => (
            <CustomDatePicker
              size='small'
              fullWidth
              name="commercialGeneralLiability.expiryDate"
              label="Expiry Date"
              value={form.watch('commercialGeneralLiability.expiryDate')}
              onChange={handleDateChange}
              error={!!form.formState.errors.commercialGeneralLiability?.expiryDate}
              helperText={form.formState.errors.commercialGeneralLiability?.expiryDate?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="commercialGeneralLiability.amount"
          control={form.control}
          render={({ field }) => (
            <NumericInput
              size='small'
              {...field}
            error={!!form.formState.errors.commercialGeneralLiability?.amount}
              helperText={form.formState.errors.commercialGeneralLiability?.amount?.message}
              fullWidth
              label="Amount"
            />
          )}
        />
      </Grid>

      {/* Automobile Liability */}
      <Grid item xs={12}>
        <Typography fontSize={{xs:13, md:14}} sx={{ mt: 0, fontWeight: '600'}}>
          Automobile Liability
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="automobileLiability.issueDate"
          control={form.control}
          render={({ field }) => (
            <CustomDatePicker
              size='small'
              fullWidth
              name="automobileLiability.issueDate"
              label="Issue Date"
              value={form.watch('automobileLiability.issueDate')}
              onChange={handleDateChange}
              error={!!form.formState.errors.automobileLiability?.issueDate}
              helperText={form.formState.errors.automobileLiability?.issueDate?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="automobileLiability.expiryDate"
          control={form.control}
          render={({ field }) => (
            <CustomDatePicker
              size='small'
              fullWidth
              name="automobileLiability.expiryDate"
              label="Expiry Date"
              value={form.watch('automobileLiability.expiryDate')}
              onChange={handleDateChange}
              error={!!form.formState.errors.automobileLiability?.expiryDate}
              helperText={form.formState.errors.automobileLiability?.expiryDate?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="automobileLiability.amount"
          control={form.control}
          render={({ field }) => (
            <NumericInput
              size='small'
              {...field}
              error={!!form.formState.errors.automobileLiability?.amount}
              helperText={form.formState.errors.automobileLiability?.amount?.message}
               fullWidth
              label="Amount"
            />
          )}
        />
      </Grid>

      {/* Cargo Liability */}
      <Grid item xs={12}>
        <Typography fontSize={{xs:13, md:14}} sx={{ mt: 0, fontWeight: '600'}}>
          Cargo Liability
        </Typography>
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="cargoLiability.issueDate"
          control={form.control}
          render={({ field }) => (
            <CustomDatePicker
              size='small'
              fullWidth
              name="cargoLiability.issueDate"
              label="Issue Date"
              value={form.watch('cargoLiability.issueDate')}
              onChange={handleDateChange}
              error={!!form.formState.errors.cargoLiability?.issueDate}
              helperText={form.formState.errors.cargoLiability?.issueDate?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="cargoLiability.expiryDate"
          control={form.control}
          render={({ field }) => (
            <CustomDatePicker
              size='small'
              fullWidth
              name="cargoLiability.expiryDate"
              label="Expiry Date"
              value={form.watch('cargoLiability.expiryDate')}
              onChange={handleDateChange}
              error={!!form.formState.errors.cargoLiability?.expiryDate}
              helperText={form.formState.errors.cargoLiability?.expiryDate?.message}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <Controller
          name="cargoLiability.amount"
          control={form.control}
          render={({ field }) => (
            <NumericInput
              size='small'
              {...field}
              error={!!form.formState.errors.cargoLiability?.amount}
              helperText={form.formState.errors.cargoLiability?.amount?.message}
              fullWidth
              label="Amount"
            />
          )}
        />
      </Grid>

      <Divider className="custom-divider" sx={{ my:2}}/>
      <Grid item xs={12}>
        <Typography fontSize={{xs:13, md:14}} sx={{ mt: 0, mb: 1.5, fontWeight:'600',}}>
          Insurer Documents
        </Typography>
      </Grid>
      <Grid item xs={12} md={12} className='pt-0'>
        <InsurerDocsUploadComponent />
      </Grid>
    </Grid>
  );
};

export default InsuranceForm;
